import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormArray } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';

import { PaciensService } from '../../services/paciens.service';
import { PaciensVmForCreate } from '../../models/paciens.model';
import { Nem } from '../../models/nem.enum';

@Component({
    selector: 'rendelo-paciens-edit',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatSelectModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatSnackBarModule,
        MatProgressSpinnerModule,
        MatIconModule
    ],
    templateUrl: './paciens-edit.component.html',
    styleUrls: ['./paciens-edit.component.scss']
})
export class PaciensEditComponent implements OnInit {
    paciensId: string | null = null;
    isNewMode = false;
    paciensForm!: FormGroup;
    loading = false;

    nemOptions = [
        { value: Nem.Ferfi, label: 'Férfi' },
        { value: Nem.No, label: 'Nő' },
        { value: Nem.Ismeretlen, label: 'Ismeretlen' }
    ];

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private fb: FormBuilder,
        private paciensService: PaciensService,
        private snackBar: MatSnackBar
    ) { }

    ngOnInit(): void {
        this.paciensId = this.route.snapshot.paramMap.get('id');
        this.isNewMode = this.paciensId === '0';
        this.initForm();
    }

    initForm(): void {
        this.paciensForm = this.fb.group({
            elotag: [''],
            vezetekNev: ['', Validators.required],
            keresztNev1: ['', Validators.required],
            keresztNev2: [''],
            utoTag: [''],
            nem: [Nem.Ismeretlen, Validators.required],
            tajszam: ['', [Validators.required, Validators.pattern(/^\d{9}$/)]],
            szuletett: [''],
            szuletesiNev: [''],
            // Arrays for collections
            telefonok: this.fb.array([]),
            emailek: this.fb.array([]),
            nyelvIds: this.fb.array([]),
            finanszirozoCegek: this.fb.array([])
        });
    }

    get pageTitle(): string {
        return this.isNewMode ? 'Új páciens' : 'Páciens módosítás';
    }

    get telefonok(): FormArray {
        return this.paciensForm.get('telefonok') as FormArray;
    }

    get emailek(): FormArray {
        return this.paciensForm.get('emailek') as FormArray;
    }

    addTelefon(): void {
        const telefonGroup = this.fb.group({
            telefonTipus: [10, Validators.required],
            orszagHivoszam: [36, Validators.required],
            korzet: ['', Validators.required],
            szam: ['', Validators.required],
            mellek: [''],
            isSms: [false],
            isPrimary: [this.telefonok.length === 0]
        });
        this.telefonok.push(telefonGroup);
    }

    removeTelefon(index: number): void {
        this.telefonok.removeAt(index);
    }

    addEmail(): void {
        const emailGroup = this.fb.group({
            emailTipus: [10, Validators.required],
            emailCim: ['', [Validators.required, Validators.email]],
            isPrimary: [this.emailek.length === 0]
        });
        this.emailek.push(emailGroup);
    }

    removeEmail(index: number): void {
        this.emailek.removeAt(index);
    }

    onSubmit(): void {
        if (this.paciensForm.invalid) {
            this.snackBar.open('Kérem töltse ki a kötelező mezőket!', 'Bezár', {
                duration: 3000,
                panelClass: ['error-snackbar']
            });
            return;
        }

        this.loading = true;
        const formValue = this.paciensForm.value;

        // Convert date to string format if needed
        const szuletett = formValue.szuletett ? new Date(formValue.szuletett).toISOString().split('T')[0] : null;

        // Transform emailek: convert number emailTipus to EmailTipusVm object
        const emailek = formValue.emailek.map((email: any) => ({
            emailTipus: {
                id: email.emailTipus,
                nev: '' // Backend should handle the type name lookup
            },
            emailCim: email.emailCim,
            isPrimary: email.isPrimary
        }));

        const paciensData: PaciensVmForCreate = {
            elotag: formValue.elotag || null,
            vezetekNev: formValue.vezetekNev,
            keresztNev1: formValue.keresztNev1,
            keresztNev2: formValue.keresztNev2 || null,
            utoTag: formValue.utoTag || null,
            nem: formValue.nem,
            tajszam: formValue.tajszam,
            szuletett: szuletett,
            szuletesiNev: formValue.szuletesiNev || null,
            lakCim: null, // TODO: Implement address form
            telefonok: formValue.telefonok,
            emailek: emailek, // Use transformed email data
            nyelvIds: formValue.nyelvIds,
            finanszirozoCegek: formValue.finanszirozoCegek
        };

        this.paciensService.addPaciens(paciensData).subscribe({
            next: (paciensId) => {
                this.loading = false;
                this.snackBar.open('Páciens sikeresen létrehozva!', 'Bezár', {
                    duration: 3000,
                    panelClass: ['success-snackbar']
                });
                this.router.navigate(['/paciens/detail', paciensId]);
            },
            error: (error) => {
                this.loading = false;
                console.error('Error creating paciens:', error);
                this.snackBar.open('Hiba történt a páciens létrehozása során!', 'Bezár', {
                    duration: 5000,
                    panelClass: ['error-snackbar']
                });
            }
        });
    }

    onCancel(): void {
        this.router.navigate(['/paciens']);
    }
}
